import React, { useEffect, useState } from "react";
import { Battery, Zap, RefreshCw, Clock, Sun } from "lucide-react";

export interface Solution {
  id: string;
  name: string;
  displayName: string;
  image: string;
  keyFeatures: {
    capacity?: string;
    output?: string;
    recharge?: string;
    efficiency?: string;
    lifespan?: string;
  };
  features: string[];
}

export type SolutionCategory =
  | "BESS"
  | "EV Infrastructure"
  | "Home"
  | "Ships"
  | "Tools"
  | "Agriculture"
  | "Defense"
  | "UAV";

interface SolutionsSectionProps {
  solution: Solution;
  isActive?: boolean;
  id?: string;
}

const SolutionsSection: React.FC<SolutionsSectionProps> = ({ 
  solution, 
  isActive = true,
  id 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const isHomeSolution = solution.name === "Home";

  const keyFeatureIcons: Record<string, React.ReactNode> = {
    capacity: <Battery className="w-5 h-5" />,
    output: <Zap className="w-5 h-5" />,
    recharge: <RefreshCw className="w-5 h-5" />,
    efficiency: <Sun className="w-5 h-5" />,
    lifespan: <Clock className="w-5 h-5" />,
  };

  const featureImages = [
    {
      image: "/solutions/firsthome.png",
      alt: "Whole Home Backup Power",
    },
    {
      image: "/solutions/solarhome.png",
      alt: "Solar Energy Storage",
    },
    {
      image: "/solutions/custombms.png",
      alt: "Electricity Bill Savings",
    },
    {
      image: "/solutions/third.png",
      alt: "Smart Home Integration",
    },
    {
      image: "/solutions/lightweight.png",
      alt: "Quiet Operation",
    },
    {
      image: "/solutions/fourth.png",
      alt: "Easy Installation",
    },
    {
      image: "/solutions/sixth.png",
      alt: "Property Value Increase",
    },
    {
      image: "/solutions/seven.png",
      alt: "Mobile App Control",
    },
  ];

  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
    }
  }, [isActive]);

  return (
    <div 
      id={id} 
      className={`space-y-1 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    >
      <div className="bg-stone-800 shadow-lg text-white">
        <div className="lg:p-12 p-4 py-20">
          <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
            <div className="lg:w-3/5 w-full">
              <div className="rounded-2xl h-full overflow-hidden relative">
                <img
                  src={isHomeSolution ? "/solutions/homesolution.png" : solution.image}
                  alt={solution.displayName}
                  className="w-full h-[550px] object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </div>

            <div className="lg:w-3/5">
              {isHomeSolution ? (
                <div className="flex flex-col justify-center h-full lg:pl-28">
                  <div className="mb-8">
                    <div className="text-4xl font-bold mb-6 text-center lg:text-left">
                      Portable Power Station
                    </div>
                    <p className="text-gray-300 text-lg text-center lg:text-left">
                      A reliable, compact power source for all your energy needs.
                    </p>
                  </div>

                  <div className="flex justify-center lg:justify-start mb-8">
                    <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors duration-300 transform hover:scale-105 active:scale-95">
                      Shop Now
                    </button>
                  </div>

                  <div className="mt-2">
                    <h3 className="text-xl font-semibold text-white mb-4 text-center lg:text-left">
                      Key Features
                    </h3>
                    <div className="space-y-4">
                      {solution.keyFeatures.capacity && (
                        <div className="flex items-center justify-center lg:justify-start animate-fadeIn">
                          <div className="w-8 h-8 bg-blue-500 rounded-full mr-4 flex items-center justify-center">
                            {keyFeatureIcons.capacity}
                          </div>
                          <span className="text-gray-200 text-lg">
                            <strong className="text-white">Capacity:</strong>{" "}
                            {solution.keyFeatures.capacity}
                          </span>
                        </div>
                      )}
                      {solution.keyFeatures.output && (
                        <div className="flex items-center justify-center lg:justify-start animate-fadeIn delay-100">
                          <div className="w-8 h-8 bg-blue-500 rounded-full mr-4 flex items-center justify-center">
                            {keyFeatureIcons.output}
                          </div>
                          <span className="text-gray-200 text-lg">
                            <strong className="text-white">Output:</strong>{" "}
                            {solution.keyFeatures.output}
                          </span>
                        </div>
                      )}
                      {solution.keyFeatures.recharge && (
                        <div className="flex items-center justify-center lg:justify-start animate-fadeIn delay-200">
                          <div className="w-8 h-8 bg-blue-500 rounded-full mr-4 flex items-center justify-center">
                            {keyFeatureIcons.recharge}
                          </div>
                          <span className="text-gray-200 text-lg">
                            <strong className="text-white">Recharge:</strong>{" "}
                            {solution.keyFeatures.recharge}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                  <div className="text-center">
                    <div className="inline-block bg-yellow-100 text-yellow-800 text-sm font-semibold px-4 py-2 rounded-full mb-6 animate-pulse">
                      Coming Soon...
                    </div>
                    <div className="text-3xl font-bold mb-4">
                      {solution.displayName}
                    </div>
                    <p className="text-gray-300 text-lg mb-6 max-w-md mx-auto">
                      We're currently working on this solution. Please check
                      back soon for updates!
                    </p>
                    <p className="text-gray-400">
                      Interested in this solution?{" "}
                      <a
                        href="#"
                        className="text-blue-400 hover:text-blue-300 underline transition-colors"
                      >
                        Contact us
                      </a>{" "}
                      for more information.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isHomeSolution && (
        <>
          <div className="bg-white px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 text-center mb-6 sm:mb-8">
              Solution Features
            </h3>
            <div className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
              {solution.features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-fadeIn"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="mb-3 sm:mb-4 flex justify-center">
                    <div className="w-full max-w-[140px] sm:max-w-[180px] md:max-w-[200px] h-12 sm:h-14 md:h-16 overflow-hidden">
                      <img
                        src={
                          featureImages[index]?.image ||
                          "/placeholder-image.png"
                        }
                        alt={
                          featureImages[index]?.alt || `Feature ${index + 1}`
                        }
                        className="w-full h-full object-contain transition-transform duration-300 hover:scale-110"
                      />
                    </div>
                  </div>

                  <div className="text-center">
                    <span className="text-sm sm:text-base md:text-lg text-gray-800 font-medium leading-tight line-clamp-2 sm:line-clamp-3">
                      {feature}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <section className="relative w-full h-[520px] md:h-[600px] bg-black overflow-hidden">
            <img
              src="/aboutsustain.png"
              alt="Smart Home Background"
              className="absolute inset-0 w-full h-full object-cover object-right lg:object-center opacity-80 transition-transform duration-700 hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>

            <div className="relative max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
              <div className="max-w-xl text-white space-y-6">
                <h2 className="text-4xl md:text-5xl font-extrabold leading-tight drop-shadow-lg">
                  Power at Your <br /> Fingertips
                </h2>

                <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
                  Monitor, control and optimize your energy usage anytime with
                  real-time insights and smart automation on the Spectrum App.
                </p>
              </div>
            </div>
          </section>

          <section className="w-full bg-white py-16">
            <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-24">
              <div className="border-l-4 border-sky-500 pl-5 animate-fadeIn">
                <h3 className="text-2xl font-extrabold text-gray-900 leading-snug">
                  Energy Insights and
                  <br />
                  Health Check
                </h3>
                <p className="mt-3 text-gray-600 text-base leading-relaxed">
                  Monitor real-time energy use and keep every appliance
                  performing at its best.
                </p>
              </div>

              <div className="border-l-4 border-sky-500 pl-5 animate-fadeIn delay-200">
                <h3 className="text-2xl font-extrabold text-gray-900 leading-snug">
                  Multiple Modes for
                  <br />
                  Every Need
                </h3>
                <p className="mt-3 text-gray-600 text-base leading-relaxed">
                  Switch modes to match your power needs from everyday use to
                  high-load performance.
                </p>
              </div>

              <div className="border-l-4 border-sky-500 pl-5 animate-fadeIn delay-400">
                <h3 className="text-2xl font-extrabold text-gray-900 leading-snug">
                  Device Control and
                  <br />
                  Scheduling
                </h3>
                <p className="mt-3 text-gray-600 text-base leading-relaxed">
                  Control, schedule, and analyse appliances like geysers and ACs
                  all from one place.
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default SolutionsSection;